import { useId, type CSSProperties, type ReactNode } from 'react';
import { IconClose } from './icons';
import './result-notice.css';

/*
 * Confeti con la forma de la marca: cuadrados redondeados en sus tres colores. Las posiciones
 * salen del ángulo áureo y no de Math.random, así que el render es puro y la ráfaga, siempre igual.
 */
const COLORS = ['var(--ink-blue)', 'var(--ink-magenta)', 'var(--ink-teal)'];

const PIECES = Array.from({ length: 28 }, (_, index) => {
  const angle = ((index * 137.508) % 360) * (Math.PI / 180);
  const distance = 56 + ((index * 53) % 72);

  return {
    '--x': `${Math.round(Math.cos(angle) * distance)}px`,
    '--y': `${Math.round(Math.sin(angle) * distance * 0.7 - 24)}px`,
    '--r': `${(index * 97) % 360}deg`,
    '--delay': `${(index % 7) * 30}ms`,
    '--size': `${5 + (index % 3) * 2}px`,
    '--color': COLORS[index % COLORS.length],
  } as CSSProperties;
});

type ResultNoticeProps = {
  title: string;
  /** `pending` es para lo que queda en manos del equipo: un reloj en vez del check. */
  tone?: 'success' | 'pending';
  /** Solo para un pago: un reembolso no se celebra. */
  celebrate?: boolean;
  children: ReactNode;
  actions?: ReactNode;
  onClose: () => void;
};

export function ResultNotice({ title, tone = 'success', celebrate = false, children, actions, onClose }: ResultNoticeProps) {
  const titleId = useId();

  return (
    <div className={`result-notice result-notice--${tone}`} role="status" aria-labelledby={titleId}>
      <div className="result-notice__icon" aria-hidden="true">
        <svg viewBox="0 0 48 48">
          <circle cx="24" cy="24" r="22" className="result-notice__ring" />
          <path
            d={tone === 'success' ? 'M14.5 24.5l6.5 6.5 12.5-13' : 'M24 13.5V24l7 4.5'}
            pathLength={1}
            className="result-notice__mark"
          />
        </svg>
        {celebrate && (
          <span className="result-notice__confetti">
            {PIECES.map((style, index) => (
              <i key={index} style={style} />
            ))}
          </span>
        )}
      </div>

      <div>
        <h2 id={titleId}>{title}</h2>
        <div className="result-notice__text">{children}</div>
        {actions && <div className="result-notice__actions">{actions}</div>}
      </div>

      <button type="button" className="result-notice__close" aria-label="Cerrar aviso" onClick={onClose}>
        <IconClose />
      </button>
    </div>
  );
}
